<template>
  <div :class="['cf-modifier-groups__group', { 'cf-modifier-groups__group--invalid': !isValid }]">
    <cf-expandable-section :auto-expand="isAutoExpanded">
      <template #title>
        <div
          id="group-label-section"
          class="cf-flex cf-flex--row cf-flex--justify-space-between"
          :aria-invalid="!isValid"
        >
          <!-- Modifier Group Label -->
          <h3 class="cf-title cf-title--3">
            {{ label }}
          </h3>

          <!-- Modifier Group Requirements -->
          <div
            v-if="!readOnly"
            class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-16"
          >
            <h5
              v-if="modifierGroupSelectionInstructionsText"
              class="cf-title cf-title--5"
            >
              {{ modifierGroupSelectionInstructionsText }}
            </h5>

            <h5
              v-if="modiferQuantityText"
              class="cf-title cf-title--5"
            >
              {{ modiferQuantityText }}
            </h5>
          </div>
        </div>
      </template>

      <template #content>
        <div
          :class="[
            'cf-checkbox-group',
            {
              'cf-checkbox-group--read-only': readOnly,
              'cf-checkbox-group--radio': isSingleSelect,
              'cf-checkbox-group--radio-required': isSingleSelect && isRequired,
              'cf-checkbox-group--invalid': !isValid
            }]"
        >
          <div class="cf-checkbox-group__items">
            <template v-for="modifier in modifierGroup.modifiers">
              <div
                v-if="selections[modifier.menuItemId]"
                :key="modifier.menuItemId"
                :class="['cf-checkbox-group__item', {
                  'cf-checkbox-group__item--arrow': checkIsNestedModifier(modifier),
                  'cf-checkbox-group__item--selected': modifierHasSelectedNestedModifiers(modifier)
                }]"
              >
                <template v-if="checkIsNestedModifier(modifier)">
                  <!-- "Combo" modifier button selection -->
                  <button
                    type="button"
                    class="cf-checkbox-group__label"
                    @click="optionClickHandler(modifier)"
                  >
                    <!-- Modifier Img -->
                    <div
                      v-if="modifier.images && modifier.images[0]"
                      class="cf-checkbox-group__label-image"
                      :style="`background-image: url(${ modifier.images[0].url })`"
                    />

                    <div class="cf-checkbox-group__label-content">
                      <!-- Modifier Name -->
                      <p class="cf-checkbox-group__label-title">
                        {{ modifier.displayName }}
                      </p>

                      <span
                        v-if="getCalories(modifier)"
                        class="cf-checkbox-group__label-description"
                      >
                        {{ getCalories(modifier) }}
                      </span>

                      <!-- Modifier's selected nested modifiers -->
                      <p class="smaller no-wrap-ellipsis">
                        {{ getModifierContent(modifier).selectedModifiersDisplay }}
                      </p>
                    </div>

                    <!-- Modifier price -->
                    <span
                      v-if="modifier.price > 0 && !hidePrices"
                      class="cf-checkbox-group__label-description spacing-left"
                    >
                      +{{ $dollars(getModifierContent(modifier).totalPrice) }}
                    </span>
                  </button>
                </template>

                <template v-else>
                  <!-- Checkbox selector -->
                  <input
                    :id="getInputName(modifier)"
                    v-model="selections[modifier.menuItemId].isSelected"
                    class="cf-checkbox-group__input"
                    type="checkbox"
                    :name="getInputName(modifier)"
                    :disabled="disableInput(modifier)"
                    @change="optionClickHandler(modifier)"
                  >
                  <label
                    class="cf-checkbox-group__label"
                    :for="getInputName(modifier)"
                  >
                    <!-- Modifier Img -->
                    <div
                      v-if="modifier.images && modifier.images[0]"
                      class="cf-checkbox-group__label-image"
                      :style="`background-image: url(${ modifier.images[0].url })`"
                    />

                    <div class="cf-checkbox-group__label-content">
                      <!-- Modifier Name -->
                      <span class="cf-checkbox-group__label-title">
                        {{ modifier.displayName }}
                      </span>

                      <span
                        v-if="getCalories(modifier)"
                        class="checkbox-group__label-description"
                      >
                        {{ getCalories(modifier) }}
                      </span>
                    </div>

                    <!-- Modifier Decrement/Increment quantity selector -->
                    <transition name="fade-left-only-in">
                      <cf-quantity-input
                        v-if="showModifierQuantityInput(modifier)"
                        v-model="selections[modifier.menuItemId].quantity"
                        class="no-spacing"
                        :max-value="getModifierMaxAllowedValue(modifier)"
                        name="menuItemQuantity"
                        size="small"
                      />
                    </transition>

                    <!-- Modifier price -->
                    <span
                      v-if="modifier.price > 0 && !hidePrices"
                      class="cf-checkbox-group__label-description spacing-left"
                    >
                      +{{ $dollars(getModifierContent(modifier).totalPrice) }}
                    </span>
                  </label>
                </template>
              </div>
            </template>
          </div>
        </div>
      </template>
    </cf-expandable-section>

    <modal
      v-slot="{ data }"
      :name="modalName"
      :display-header-title="false"
      :has-back-arrow="true"
    >
      <cf-modifier-menu-item-child
        :allow-deselect="true"
        :item="data.item"
        :read-only="readOnly"
        :selected-modifiers="data.selectedModifiers"
        :selected-quantity="data.selectedQuantity"
        @close="$closeModal(modalName)"
        @deselect="deselect"
        @update="updatedChildHandler"
      />
    </modal>
  </div>
</template>


<script>
  import CfExpandableSection from '@/view/components/shared/cf-expandable-section/index.vue';
  import CfQuantityInput from '@/view/components/shared/cf-quantity-input/index.vue';
  import CfModifierMenuItemChild from '@/view/components/globals/cf-modifier-menu-item-child/index.vue';


  export default {
    name: 'CfModifierGroupSelection',

    components: {
      CfExpandableSection,
      CfQuantityInput,
      CfModifierMenuItemChild
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      hidePrices: {
        type: Boolean,
        default: false
      },

      label: {
        type: String,
        required: true
      },

      modifierGroup: {
        type: Object,
        required: true
      },

      name: {
        type: String,
        required: true
      },

      readOnly: {
        type: Boolean,
        default: false
      },

      selectedModifiers: {
        type: Array,
        default: null
      },

      priceOverrideSourceModifierId: {
        type: Number,
        default: null
      }
    },

    data() {
      return {
        inputIdUniqueifier: Date.now(),
        selections: {},
        isPriceOverrideGroup: false,
        isValid: true
      };
    },

    computed: {
      isAutoExpanded() {
        return !this.modifierGroup.isCollapsed;
      },

      isRequired() {
        return this.modifierGroup.minRequired > 0;
      },

      isSingleSelect() {
        return this.modifierGroup.maxAllowed === 1;
      },

      isUnlimited() {
        return this.modifierGroup.maxAllowed === -1;
      },

      modifierGroupSelectionInstructionsText() {
        const hasMaxQuantitySelection = !this.isRequired && !this.isUnlimited;

        const hasRangeQuantitySelection = this.isRequired && !this.isUnlimited;

        const hasMinQuantitySelection = this.isRequired && this.isUnlimited;

        const { maxAllowed, minRequired } = this.modifierGroup;

        switch (true) {
          case this.isSingleSelect:
            return this.isRequired ? 'Required' : 'Select One';

          case hasMaxQuantitySelection:
            return `Up to ${maxAllowed}`;

          case hasMinQuantitySelection:
            return `At least ${minRequired}`;

          case hasRangeQuantitySelection:
            return `Between ${minRequired} and ${maxAllowed}`;

          default:
            return '';
        }
      },

      modifierCounts() {
        return Object.values(this.selections).reduce((counts, { isSelected, quantity }) => {
          if (isSelected) {
            counts.selection += 1;
            counts.quantity += quantity;
          }

          return counts;
        }, { selection: 0, quantity: 0 });
      },

      modiferQuantityText() {
        const renderQuantityText = this.modifierGroup.maxAllowed >= 1 && this.modifierGroup.maxModifierQtyAllowed > 1 && this.modifierGroup.minModifierQtyRequired >= 1;

        return renderQuantityText && !this.readOnly ? `${this.modifierCounts.quantity} of ${this.modifierGroup.maxModifierQtyAllowed} selected` : '';
      },

      modalName() {
        return `${this.name}-menuItemChild`;
      },

      noneModifierMenuItemId() {
        return this.modifierGroup.modifiers.find(modifier => modifier.modifierTemplate === 'NoneModifierTemplate')?.menuItemId;
      },

      isVirtualModifierGroup() {
        return this.modifierGroup.isVirtual;
      }
    },

    watch: {
      selections: {
        handler: 'selectionsChangeHandler',
        deep: true
      }
    },

    mounted() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      createdHandler() {
        this.parseModifierGroup();

        this.validatedForm.addValidator({
          name: this.name,
          validate: this.validate
        });
      },

      destroyedHandler() {
        this.validatedForm.removeValidator(this.name);
      },

      parseModifierGroup() {
        // Note: Logic applies to dynamic modifier pricing!
        // If the current modifierGroup is priceOverrideGroup, we know
        // this modifier group will dynamically drive the changes for other the modifier group's individual modifier pricing.
        if (this.modifierGroup.priceOverrideGroup) {
          this.isPriceOverrideGroup = true;
        }

        this.selections = this.parseModifierGroupSelections(this.modifierGroup);
      },

      parseModifierGroupSelections(modifierGroup) {
        const selections = {};

        modifierGroup.modifiers.forEach((modifier) => {
          const isNoneModifier = modifier.menuItemId === this.noneModifierMenuItemId;
          const priceOverrides = modifier.priceOverrides?.length > 0
            ? modifier.priceOverrides
            : [];

          let isSelected = false;
          let quantity = 1;
          let modifiers = null;

          if (modifier.modifierGroups.length > 0 && modifier.isDefault) {
            modifiers = this.parseNestedModiferGroup(modifier);
          }

          /**
           * Sets the selection states of the modifiers based on the existing
           * modifier selections of the order item being edited.
           */
          if (this.selectedModifiers) {
            const selectedModifier = this.selectedModifiers.find(m => m.menuItemId === modifier.menuItemId);

            if (selectedModifier) {
              isSelected = true;

              quantity = selectedModifier.quantity;

              if (selectedModifier.modifiers?.length) {
                modifiers = selectedModifier.modifiers;
              }
            }
          }

          else if (modifier.isDefault) {
            isSelected = true;
          }

          if (isSelected) {
            // Emit to parent the source modifier id if exists within the priceOverrideGroup and is a default modifier for the initial modifier selections on render.
            if (this.isPriceOverrideGroup) {
              this.$emit('set-price-override-source-modifer-id', modifier.modifierId);
            }
          }

          selections[modifier.menuItemId] = {
            isSelected,
            quantity,
            isNoneModifier,
            priceOverrides,
            modifierGroupId: modifierGroup.modifierGroupId,
            menuItemGroupItemId: modifier.menuItemGroupItemId || null,
            ...modifiers && { modifiers }
          };
        });

        return selections;
      },

      parseNestedModiferGroup(modifer) {
        const modifiers = [];

        modifer.modifierGroups.forEach((_modGroup) => {
          _modGroup.modifiers.forEach((_modifier) => {
            const modifierData = {
              menuItemId: Number(_modifier.menuItemId),
              isVirtual: false,
              menuItemGroupItemId: _modifier.menuItemGroupItemId,
              modifierGroupId: _modGroup.modifierGroupId,
              quantity: 1
            };

            if (_modifier.isDefault) {
              modifiers.push(modifierData);
            }
          });
        });

        return modifiers;
      },

      getCalories(modifier) {
        return modifier.nutritionalInfo?.calories;
      },

      checkIsNestedModifier(modifier) {
        return this.selections[modifier.menuItemId] && modifier.modifierGroups?.length > 0;
      },

      modifierHasSelectedNestedModifiers(modifier) {
        const isModiferSelected = this.selections[modifier.menuItemId]?.isSelected;

        return this.selections[modifier.menuItemId]?.modifiers?.length && isModiferSelected;
      },

      getModifierContent(modifier) {
        const modifierContent = {
          totalPrice: modifier.price,
          selectedModifiersDisplay: 'Select to customize...'
        };

        const hasPriceOverrides = modifier.priceOverrides?.length > 0;
        // Note: Logic applies to dynamic modifier pricing!
        if (hasPriceOverrides) {
          // If a modifier has price overrides, find match between the _priceOverride.sourceModifierId and priceOverrideSourceModifierId.
          // If a match is found, update the total price to to display the modifiers price.
          const priceOverride = modifier.priceOverrides.find(_priceOverride => _priceOverride.sourceModifierId === this.priceOverrideSourceModifierId);

          if (priceOverride) {
            modifierContent.totalPrice = priceOverride.price;
          }
        }

        const selectedNestedModifiers = this.selections[modifier.menuItemId]?.modifiers;

        if (selectedNestedModifiers?.length) {
          let allMenuModifiers = [];

          modifier.modifierGroups.forEach((group) => {
            allMenuModifiers = allMenuModifiers.concat(group.modifiers);
          });

          modifierContent.selectedModifiersDisplay = '';

          selectedNestedModifiers.forEach((selectedMod) => {
            const menuModifier = allMenuModifiers.find(m => m.menuItemId === selectedMod.menuItemId);

            const modifierQuantitySubstring = selectedMod.quantity > 1 ? ` (${selectedMod.quantity})` : '';

            modifierContent.selectedModifiersDisplay += `${menuModifier.description}${modifierQuantitySubstring}, `;

            modifierContent.totalPrice += menuModifier.price * selectedMod.quantity;
          });
        }

        modifierContent.selectedModifiersDisplay = modifierContent.selectedModifiersDisplay.replace(/,\s$/, '');

        return modifierContent;
      },

      showModifierQuantityInput(modifier) {
        const { maxModifierQtyAllowed } = this.modifierGroup;
        const { maxAllowed } = modifier;
        const allowsModifierGroupQuantity = maxModifierQtyAllowed > 1 || maxModifierQtyAllowed === -1;
        const allowsModifierQuantity = maxAllowed > 1 || maxAllowed === -1;

        return !this.readOnly && allowsModifierGroupQuantity && allowsModifierQuantity && this.selections[modifier.menuItemId].isSelected;
      },

      getModifierMaxAllowedValue(modifier) {
        /** maxModifierQtyAllowed === -1 means no limit so fallback to the modifier's maxAllowed  */
        if (this.modifierGroup.maxModifierQtyAllowed === -1) return modifier.maxAllowed;

        const currentModifierQuantity = this.selections[modifier.menuItemId].quantity;

        const totalQuantityRemainder = this.modifierGroup.maxModifierQtyAllowed - this.modifierCounts.quantity;

        const modifierAvailableMax = totalQuantityRemainder + currentModifierQuantity;

        return Math.min(modifier.maxAllowed, modifierAvailableMax);
      },

      disableInput(modifier) {
        const { isSelected } = this.selections[modifier.menuItemId];

        const { maxModifierQtyAllowed, maxAllowed } = this.modifierGroup;

        const isSingleQuantity = maxModifierQtyAllowed === 1;

        const isUnlimitedQuantity = maxModifierQtyAllowed === -1;

        const hasReachedMaxQuantity = !isSingleQuantity && !isUnlimitedQuantity && maxModifierQtyAllowed <= this.modifierCounts.quantity;

        const hasReachedMaxSelection = !this.isSingleSelect && !this.isUnlimited && maxAllowed <= this.modifierCounts.selection;

        const isNoneModifier = modifier.menuItemId === this.noneModifierMenuItemId;

        const disabled = isSelected
          ? this.isSingleSelect && this.isRequired
          : ((hasReachedMaxSelection || hasReachedMaxQuantity) && !isNoneModifier);

        return disabled;
      },

      optionClickHandler(modifier) {
        // Note: Logic applies to dynamic modifier pricing!
        // If current modifier group isPriceOverrideGroup, then any selected modifiers within this group will determine the other modifier groups' individual modifier pricing.
        // Example: "Large", "Med" or "Mini" modifiers within the price-override group will dynamically update the pricing of the modifiers within "Added Cheese", or "Extra Meat",
        // if the modifiers within these groups are configured with dynamic prices (ie. modifier.priceOverrides).
        if (this.isPriceOverrideGroup) {
          // If selected modifier exists within price override group, emit to parent the modifier that is selected, for which the other modifier groups will reference
          // to update the pricing of its individual modifiers.
          this.$emit('set-price-override-source-modifer-id', modifier.modifierId);
        }

        Object.keys(this.selections).forEach((key) => {
          /** if the "none modifier" is selected, deselect all other modifiers */
          if (modifier.menuItemId === this.noneModifierMenuItemId) {
            if (Number(key) !== Number(modifier.menuItemId)) {
              this.selections[key].isSelected = false;

              this.$emit('set-modifier-with-price-override', modifier, true);

              delete this.selections[key].modifiers;
            }
          }
          /** unselect the "none modifier" if one exists when any other modifier is selected */
          else if (this.selections[key].isNoneModifier) {
            this.selections[key].isSelected = false;
          }

          /* reset the quantity of all non-selected modifiers to 1 */
          if (!this.selections[key].isSelected) {
            this.selections[key].quantity = 1;
          }

          /* if only a single modifier selection is allowed, deselect all other modifiers */
          if (this.isSingleSelect && Number(key) !== Number(modifier.menuItemId)) {
            this.selections[key].isSelected = false;

            if (this.selections[key]?.modifiers?.length) {
              this.selections[key].modifiers = [];
            }

            this.$emit('set-modifier-with-price-override', modifier, true);
          }
        });

        if (modifier.modifierGroups?.length > 0) {
          const existingSelection = this.selections[modifier.menuItemId];

          this.$openModal(this.modalName, {
            item: modifier,
            selectedModifiers: existingSelection ? existingSelection.modifiers : null,
            selectedQuantity: existingSelection ? existingSelection.quantity : null
          });
        }
      },

      deselect(menuItemId) {
        this.updateSelection(menuItemId, { quantity: 1, isSelected: false });

        this.$closeModal(this.modalName);
      },

      updatedChildHandler(child) {
        if (child.modifiers?.length) {
          this.updateSelection(child.item.menuItemId, {
            modifiers: child.modifiers,
            quantity: child.quantity,
            isSelected: true
          });
        }
        else {
          this.updateSelection(child.item.menuItemId, {
            quantity: child.quantity,
            isSelected: false
          });
        }

        this.$closeModal(this.modalName);
      },

      updateSelection(menuItemId, value) {
        if (this.isSingleSelect) {
          this.selections = this.resetSelections(this.selections);
        }

        this.selections[menuItemId] = value;

        /*
          NOTE: The following change event dispatch will trigger the "dirty" handler for
          <validate-form>, which will unset the favorite button if the item was a favorite.
        */

        const event = new Event('change', { bubbles: true });

        this.$el.dispatchEvent(event);
      },

      resetSelections(prevSelections) {
        const updatedSelections = {};

        Object.entries(prevSelections).forEach(([key, val]) => {
          updatedSelections[key] = { ...val, isSelected: false, quantity: 1 };
        });

        return updatedSelections;
      },

      getPriceOverride(priceOverrides) {
        const hasPriceOverrides = priceOverrides?.length > 0;
        // Note: Logic applies to dynamic modifier pricing!
        // Check if a modifier has an array of priceOverrides
        if (hasPriceOverrides) {
          // If a modifier has price overrides array, find match in the array values between the priceOverrides.sourceModifierId and the top-level priceOverrideSourceModifierId
          // Note: The "source" top-level modifier (ie. this.priceOverrideSourceModifierId) is what that dynamically changes the modifiers price if priceOverrides array has values.
          const priceOverride = priceOverrides.find(override => override.sourceModifierId === this.priceOverrideSourceModifierId);

          if (priceOverride) {
            return { modifierPriceOverride: priceOverride.price, sourceModifierId: priceOverride.sourceModifierId };
          }

          return {};
        }

        return {};
      },

      getInputName(modifier) {
        return `modifier-${this.name}-${this.inputIdUniqueifier}-${modifier.menuItemId}`;
      },

      selectionsChangeHandler(selections) {
        const selectedModifiers = Object.entries(selections).reduce((array, [menuItemId, selection]) => {
          const { isSelected, modifiers, quantity, priceOverrides, menuItemGroupItemId, modifierGroupId } = selection;

          if (isSelected) {
            const { modifierPriceOverride = null, sourceModifierId = null } = this.getPriceOverride(priceOverrides);

            const selectedModifier = {
              menuItemId: Number(menuItemId),
              ...modifiers && { modifiers },
              isVirtual: this.isVirtualModifierGroup,
              menuItemGroupItemId,
              modifierGroupId,
              quantity
            };

            if (modifierPriceOverride && sourceModifierId) {
              Object.assign(selectedModifier, { modifierPriceOverride, sourceModifierId });
              // Note: Logic applies to dynamic modifier pricing!
              // Preserve the selected modifier with price overrides - ie. emit to parent - in case the user updates the priceOverrideSourceModifierId
              // if they've already selected a modifier with a price override.
              // If user changes the priceOverrideSourceModifierId then need to update the modifier group(s) with modifiers that price overrides to reflect their new modifier price to re-calculate
              // the menu items total modifier selection.
              // Example: If a user selected size "large" within the "top-level" priceOverrideGroup and an added extra cheese modifier,
              // then changed to size "medium", the extra cheese option might be priced differently.
              this.$emit('set-modifier-with-price-override', { menuItemId: Number(menuItemId), priceOverrides });
            }

            array.push(selectedModifier);
          }
          else {
            // Note: Logic applies to dynamic modifier pricing!
            // Emit to parent to clear out modifier with price override regard
            this.$emit('set-modifier-with-price-override', { menuItemId }, true);
          }

          return array;
        }, []);

        if (!this.isValid) {
          this.validate();
        }

        this.$emit('selected', {
          modifierGroupId: this.modifierGroup.modifierGroupId,
          modifierGroup: this.modifierGroup,
          modifiers: selectedModifiers
        });
      },

      validate() {
        const validationStatus = this.validateSelections();

        this.isValid = validationStatus;

        return validationStatus;
      },

      validateSelections() {
        const maxAllowedSelectedGroupModifiers = this.modifierGroup.maxAllowed === -1 ? Number.MAX_VALUE : this.modifierGroup.maxAllowed;

        const isValidModifierSelection = this.modifierCounts.selection >= this.modifierGroup.minRequired && this.modifierCounts.selection <= maxAllowedSelectedGroupModifiers;

        /** maxModifierQtyAllowed === -1 results in no limit, so max modifier will always be valid, else compare selected modifier quantity against maxModifierQtyAllowed. */
        const isValidMaxModifierQuantity = this.modifierGroup.maxModifierQtyAllowed === -1 || this.modifierCounts.quantity <= this.modifierGroup.maxModifierQtyAllowed;

        /** minModifierQtyRequired === 0 results in no limit, so min modifier will always be valid, else compare selected modifier quantity against minModifierQtyRequired.  */
        const isValidMinModifierQuantity = this.modifierGroup.minModifierQtyRequired === 0 || this.modifierCounts.quantity >= this.modifierGroup.minModifierQtyRequired;

        const validationStatus = isValidModifierSelection && isValidMaxModifierQuantity && isValidMinModifierQuantity;

        return validationStatus;
      }
    }
  };
</script>
